import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getCentroMapaPadrao } from '../lib/estabelecimento';
import { carregarGoogleMaps, TEM_GOOGLE_MAPS } from '../lib/geo';

export interface PinoMapa {
  numero: string;
  titulo: string;
  tipo_manifestacao: string | null;
  status: string;
  bairro: string | null;
  lat: number;
  lng: number;
  data_abertura: string;
}

interface Props {
  pinos: PinoMapa[];
  centro?: [number, number];
  zoom?: number;
  altura?: string;
}

const STATUS_COR: Record<string, string> = {
  aberto: '#f59e0b', em_tramitacao: '#3b82f6', em_analise: '#3b82f6', em_diligencia: '#3b82f6',
  aguardando_solicitante: '#f59e0b', deferido: '#10b981', concluido: '#10b981',
  indeferido: '#ef4444', arquivado: '#94a3b8', cancelado: '#94a3b8',
};

const TIPO_LABEL: Record<string, string> = {
  sugestao: 'Sugestão', reclamacao: 'Reclamação', denuncia: 'Denúncia',
  elogio: 'Elogio', solicitacao_informacao: 'Solicitação de informação', outro: 'Outro',
};

const STATUS_LABEL: Record<string, string> = {
  aberto: 'Aberto', em_tramitacao: 'Em tramitação', em_analise: 'Em análise', em_diligencia: 'Em diligência',
  aguardando_solicitante: 'Aguardando manifestante', deferido: 'Deferido', concluido: 'Concluído',
  indeferido: 'Indeferido', arquivado: 'Arquivado', cancelado: 'Cancelado',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

function popupHtml(p: PinoMapa): string {
  const cor = STATUS_COR[p.status] ?? '#64748b';
  const tipoLabel = p.tipo_manifestacao ? TIPO_LABEL[p.tipo_manifestacao] ?? p.tipo_manifestacao : '—';
  const statusLabel = STATUS_LABEL[p.status] ?? p.status;
  const data = new Date(p.data_abertura).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return `
    <div style="font-family:-apple-system,Segoe UI,Roboto,sans-serif;min-width:220px;">
      <div style="font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;">${escapeHtml(p.numero)}</div>
      <div style="font-weight:600;color:#0f172a;margin-top:2px;font-size:14px;">${escapeHtml(p.titulo)}</div>
      <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;">
        <span style="font-size:11px;background:#f1f5f9;color:#475569;padding:2px 8px;border-radius:9999px;">${escapeHtml(tipoLabel)}</span>
        <span style="font-size:11px;background:${cor}1a;color:${cor};padding:2px 8px;border-radius:9999px;font-weight:500;">${escapeHtml(statusLabel)}</span>
      </div>
      ${p.bairro ? `<div style="font-size:12px;color:#64748b;margin-top:8px;">${escapeHtml(p.bairro)}</div>` : ''}
      <div style="font-size:11px;color:#94a3b8;margin-top:4px;">Aberto em ${data}</div>
    </div>`;
}

/** Mapa da Ouvidoria (público). Google quando há chave; senão Leaflet. */
export function MapaOuvidoria(props: Props) {
  return TEM_GOOGLE_MAPS ? <MapaGoogle {...props} /> : <MapaLeaflet {...props} />;
}

function MapaGoogle({ pinos, centro = getCentroMapaPadrao(), zoom = 12, altura = '500px' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const mapsRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoRef = useRef<any>(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    let vivo = true;
    carregarGoogleMaps().then((maps) => {
      if (!vivo) return;
      if (!maps || !containerRef.current) { setFalhou(true); return; }
      mapsRef.current = maps;
      mapRef.current = new maps.Map(containerRef.current, {
        center: { lat: centro[0], lng: centro[1] }, zoom,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false,
      });
      infoRef.current = new maps.InfoWindow();
      render();
    });
    return () => { vivo = false; mapRef.current = null; markersRef.current = []; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { render(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [pinos]);

  function render() {
    const maps = mapsRef.current;
    const map = mapRef.current;
    if (!maps || !map) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (pinos.length === 0) return;
    const bounds = new maps.LatLngBounds();
    pinos.forEach((p) => {
      const cor = STATUS_COR[p.status] ?? '#64748b';
      const pos = { lat: p.lat, lng: p.lng };
      const marker = new maps.Marker({
        position: pos, map,
        icon: { path: maps.SymbolPath.CIRCLE, scale: 8, fillColor: cor, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 },
      });
      marker.addListener('click', () => { infoRef.current.setContent(popupHtml(p)); infoRef.current.open(map, marker); });
      markersRef.current.push(marker);
      bounds.extend(pos);
    });
    if (pinos.length === 1) { map.setCenter({ lat: pinos[0].lat, lng: pinos[0].lng }); map.setZoom(15); }
    else map.fitBounds(bounds, 40);
  }

  if (falhou) return <MapaLeaflet pinos={pinos} centro={centro} zoom={zoom} altura={altura} />;
  return <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '8px' }} />;
}

function MapaLeaflet({ pinos, centro = getCentroMapaPadrao(), zoom = 12, altura = '500px' }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(centro, zoom);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; markersRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markersRef.current) return;
    markersRef.current.clearLayers();
    if (pinos.length === 0) return;
    const bounds = L.latLngBounds(pinos.map((p) => [p.lat, p.lng] as [number, number]));
    pinos.forEach((p) => {
      const cor = STATUS_COR[p.status] ?? '#64748b';
      const icone = L.divIcon({
        className: 'pino-ouvidoria',
        html: `<div style="width:22px;height:22px;border-radius:50%;background:${cor};border:3px solid #ffffff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      L.marker([p.lat, p.lng], { icon: icone }).bindPopup(popupHtml(p)).addTo(markersRef.current!);
    });
    if (pinos.length === 1) mapRef.current.setView([pinos[0].lat, pinos[0].lng], 15);
    else mapRef.current.fitBounds(bounds, { padding: [40, 40] });
  }, [pinos]);

  return <div ref={containerRef} style={{ height: altura, width: '100%', borderRadius: '8px' }} />;
}
